import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumb';
import Page from '../../components/Page';
import EntitySearchForm from '../../components/EntitySearchForm';
import Pagination from '../../components/Pagination';
import client from '../../lib/feathers';
import { formatDateOnly } from '../../lib/dateOnly';

export default function Passports() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;
  const [page, setPage] = useState(query.get('page') || 0);
  const [passports, setPassports] = useState([]);
  const [total, setTotal] = useState(0);
  const [keywords, setKeywords] = useState(query.get('keywords') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(query.get('status') || '');

  const getPassports = async () => {
    try {
      setLoading(true);
      setError(null);
      const service = client.service('admin/passport');
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: {
          startDate: -1,
        },
      };

      if (keywords && keywords.length) {
        q.title = { $iLike: `%${keywords}%` };
      }

      if (statusFilter) {
        q.status = statusFilter;
      }

      const result = await service.find({
        query: q,
      });

      setPassports(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Error loading passports:', e);
      setError('Failed to load passports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    getPassports();
  };

  const navigateToPage = (p = 0, kw) => {
    if (p * limit > total || p * limit < 0) return;
    let url = `/admin/passports?page=${p}`;
    if (kw) {
      url += `&keywords=${kw}`;
    }
    if (statusFilter) {
      url += `&status=${statusFilter}`;
    }
    navigate(url);
    setPage(p);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      ended: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  useEffect(() => {
    getPassports();
  }, [page, statusFilter]);

  return (
    <Page title="Wine Passports">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Passports' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/admin/passports/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            Create Passport
          </Link>
        </div>
      </div>

      <div className="mt-6 pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Passports ({total} Total)
          </h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="ended">Ended</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <EntitySearchForm onSearch={onSearch} setKeywords={setKeywords} keywords={keywords} />
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="flex flex-col mt-4">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Passport
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Range
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Threshold
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">View</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {passports.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                          No passports found. {keywords && 'Try adjusting your search.'}
                        </td>
                      </tr>
                    ) : (
                      passports.map((passport) => (
                        <tr
                          className="hover:bg-gray-100 cursor-pointer"
                          key={passport.id}
                          onClick={() => navigate(`/admin/passports/${passport.id}`)}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              {passport.coverImage ? (
                                <div className="flex-shrink-0 h-10 w-10">
                                  <img
                                    className="h-10 w-10 rounded object-cover"
                                    src={`${process.env.REACT_APP_ATTACHMENT_URL}/${passport.coverImage.path}`}
                                    alt=""
                                  />
                                </div>
                              ) : (
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded bg-primary pt-2 text-center text-white text-xs">
                                    PP
                                  </div>
                                </div>
                              )}
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {passport.title}
                                </div>
                                {passport.description && (
                                  <div className="text-sm text-gray-500 truncate max-w-xs">
                                    {passport.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(passport.status)}`}>
                              {passport.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div>{formatDateOnly(passport.startDate)}</div>
                            <div className="text-gray-400">to {formatDateOnly(passport.endDate)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {passport.completionThreshold} stops
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {passport.createdBy ? (
                              <div>
                                <div className="text-sm text-gray-900">
                                  {passport.createdBy.firstName} {passport.createdBy.lastName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {passport.createdBy.email}
                                </div>
                              </div>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Link
                              to={`/admin/passports/${passport.id}`}
                              className="text-primary hover:text-red-900"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}
